import {
	computed,
	ref,
	watch,
	nextTick,
	onMounted,
	ComputedRef,
} from 'vue';
import StoreApi from '@zyro-inc/site-modules/api/StoreApi';
import { useSiteGlobal } from '@zyro-inc/site-modules/use/useSiteGlobal';

import {
	PRODUCT_TYPES,
	PRODUCT_TYPES_STAGING,
	PREVIEW_SUBDOMAINS,
} from '@zyro-inc/site-modules/constants/siteModulesConstants';
import {
	EcommerceCollection,
	EcommerceProduct,
	EcommerceProductVariantQuantity,
} from '@zyro-inc/site-modules/types';

import { useEcommerceGlobal } from '@zyro-inc/site-modules/use/useEcommerceGlobal';
import {
	SiteBlockAppointmentList,
	SiteBlockEcommerceProductList,
	SiteEcommerceSortingValue,
	EcommerceProductListScrollBehaviour,
} from '@hostinger/builder-schema-validator/schema/schemaTypes';
import { getWebsiteCategoryUrl } from '@zyro-inc/site-modules/utils/ecommerce/category';
import { updatePageUrl } from '@zyro-inc/site-modules/utils/ecommerce/urlPagination';

export const useProductList = (
	{
		data,
		blockId,
		productCategoryId,
		productsPerPage,
		isMobileView,
		isInPreviewMode,
		scrollBehaviour,
		isCategoryListEnabled,
		isAppointmentList = false,
	}: {
		data: SiteBlockAppointmentList | SiteBlockEcommerceProductList,
		blockId: string,
		productCategoryId: ComputedRef<string>,
		productsPerPage: ComputedRef<number>,
		isMobileView: boolean,
        isInPreviewMode: boolean,
		scrollBehaviour: ComputedRef<EcommerceProductListScrollBehaviour>,
		isCategoryListEnabled: ComputedRef<boolean>,
		isAppointmentList?: boolean,
	},
) => {
	const {
		ecommerceStoreId,
		pageData,
	} = useSiteGlobal();

	const {
		isLoading: isEcommerceLoading,
		legacyProductPages,
		categories,
		fetchCategories,
		setCategories,
		setIsLoading,
		setIsLoaded,
		isCategoriesLoaded,
		setVariantsQuantity,
		setIsCategoriesLoaded,
		canAddToCart,
		isProductReviewEnabled,
	} = useEcommerceGlobal({
		blockId,
	});

	// During SSR, window is undefined
	const isPreviewDomainOrigin = typeof window !== 'undefined' && PREVIEW_SUBDOMAINS.filter((domain) => domain !== '.builder-preview.com').some((previewDomain) => window.origin.includes(previewDomain));

	const currentPage = ref(1);
	const products = ref<EcommerceProduct[]>([]);
	const totalProductCount = ref(0);
	const currentCategoryId = ref(productCategoryId.value);
	const variantsQuantity = ref<EcommerceProductVariantQuantity[]>([]);
	const sorting = ref<SiteEcommerceSortingValue>(
		data.productSorting?.enabled
			? data.productSorting?.sortingOptions?.find(({ isEnabled }) => isEnabled)?.value || ''
			: '',
	);
	const isInitialDataFetched = ref(false);

	const pageCount = computed(() => Math.ceil(totalProductCount.value / productsPerPage.value));

	const isBlockHidden = computed(() => (isMobileView ? data.mobile?.isHidden : data.desktop?.isHidden));

	const blockStorePageQuery = computed(() => `store-page-${blockId}`);

	const isProductListShown = computed(() => !!products.value?.length);

	const fetchListProducts = async ({
		sort,
		page,
		categoryId = currentCategoryId.value,
		toDate = pageData.value.buildDate ? new Date(Number.parseInt(pageData.value.buildDate, 10)).toISOString() : undefined,
	}: {
        sort: string;
        page: number;
        categoryId?: string;
        toDate?: string;
    }) => {
		if (!ecommerceStoreId.value) {
			return;
		}

		// !IMPORTANT to reset isLoaded for animations
		// when not all page products are loaded, only the ones in cart, when this isLoaded=true,
		// animations will not be observed on mounted in BlockUser.vue because products load later and need to be watched into
		setIsLoaded(false);
		setIsLoading(true);

		try {
			const pageOffset = scrollBehaviour.value === EcommerceProductListScrollBehaviour.PAGINATION
				? (page - 1) * productsPerPage.value
				: 0;
			const productPerPageLimit = scrollBehaviour.value === EcommerceProductListScrollBehaviour.PAGINATION
				? productsPerPage.value
				: page * productsPerPage.value;

			const appointmentProductTypeId = isPreviewDomainOrigin ? PRODUCT_TYPES_STAGING.BOOKING.typeId : PRODUCT_TYPES.BOOKING.typeId;
			const typeId = isAppointmentList ? appointmentProductTypeId : undefined;

			const productDataPromises = await Promise.allSettled([
				fetchCategories(ecommerceStoreId.value, isCategoryListEnabled.value),
				StoreApi.getStoreProducts(ecommerceStoreId.value, {
					offset: pageOffset,
					limit: productPerPageLimit,
					collectionId: categoryId,
					sort: (!sort && categoryId) ? 'order=ASC&sort_by=collection_order' : sort,
					toDate,
					typeId,
				}),
			]);

			const productDataValues = productDataPromises.map((productData) => (productData.status === 'fulfilled' ? productData.value : {}));
			const [categoriesResponse, productsResponse] = productDataValues as [
				{collections: EcommerceCollection[]},
				{products: EcommerceProduct[], count: number}
			];
			const fetchedProductIds = productsResponse?.products?.map(({ id }) => String(id)) || [];
			const quantity = fetchedProductIds.length > 0
				? await StoreApi.getVariantsQuantity(ecommerceStoreId.value, fetchedProductIds)
				: [];

			if (categoriesResponse?.collections?.length) {
				setCategories(categoriesResponse.collections.sort(
					(a: EcommerceCollection, b: EcommerceCollection) => a.title.localeCompare(b.title),
				));
			}

			products.value = productsResponse?.products || [];
			totalProductCount.value = productsResponse?.count || 0;
			variantsQuantity.value = quantity;

			setVariantsQuantity(quantity);
		} catch (error) {
			console.error(error);
		} finally {
			setIsLoading(false);
			setIsLoaded(true);
			// set categories only when all data is fetched to prevent flashing
			setIsCategoriesLoaded(true);
		}
	};

	const handlePageChange = (page: number) => {
		currentPage.value = page;

		updatePageUrl({
			page,
			pageQueryParam: blockStorePageQuery.value,
			isInPreviewMode,
		});

		fetchListProducts({
			page,
			sort: sorting.value,
			categoryId: currentCategoryId.value,
		});
	};

	const handleSortChange = (event: Event) => {
		const sortType = (
            event.target as HTMLInputElement
        ).value as SiteEcommerceSortingValue;

		sorting.value = sortType;

		fetchListProducts({
			sort: sortType,
			page: currentPage.value,
			categoryId: currentCategoryId.value,
		});
	};

	const handleCategoryChange = (id: string, page?: number) => {
		currentPage.value = 1;
		currentCategoryId.value = id;

		if (!isInPreviewMode) {
			const href = getWebsiteCategoryUrl(id);

			window.history.pushState({}, '', href);
		}

		fetchListProducts({
			sort: sorting.value,
			page: page || currentPage.value,
			categoryId: currentCategoryId.value,
		});
	};

	const handleCategoryClick = (id: string) => {
		if (currentCategoryId.value === id) {
			return;
		}

		handleCategoryChange(id);
	};

	const handleBrowserNavigationPageChange = () => {
		const params = new URLSearchParams(window.location.search);
		const pageParam = params.get(blockStorePageQuery.value) || '1';
		const pageFromParams = Number.parseInt(pageParam, 10);

		if (pageFromParams === currentPage.value) {
			return;
		}

		currentPage.value = pageFromParams;
	};

	const handleBrowserNavigationCategoryChange = () => {
		const params = new URLSearchParams(window.location.search);
		const categoryParam = params.get('category');

		if (categoryParam === currentCategoryId.value || !categoryParam) {
			return;
		}

		currentCategoryId.value = categoryParam;

		fetchListProducts({
			sort: sorting.value,
			page: currentPage.value,
			categoryId: categoryParam || '',
		});
	};

	const getInitialData = () => {
		// Handle astro server side cases
		if (typeof window === 'undefined') {
			return;
		}

		const params = new URLSearchParams(window.location.search);
		const pageParam = params.get(blockStorePageQuery.value) || '1';
		const pageFromParams = Number.parseInt(pageParam, 10);
		const categoryParam = params.get('category');
		const isPageDifferent = pageFromParams !== currentPage.value;

		if (isPageDifferent && !categoryParam) {
			currentPage.value = pageFromParams;

			handlePageChange(pageFromParams);
		} else if (categoryParam) {
			const page = isPageDifferent ? pageFromParams : currentPage.value;

			handleCategoryChange(categoryParam, page);
		} else {
			fetchListProducts({
				sort: sorting.value,
				page: currentPage.value,
				categoryId: currentCategoryId.value,
			});
		}

		isInitialDataFetched.value = true;
	};

	const handleWindowPopState = () => {
		handleBrowserNavigationPageChange();
		handleBrowserNavigationCategoryChange();
	};

	// Hidden blocks in builder has only visibility: hidden so we need to check and fetch information here on demand (aka on screen resize)
	watch(isBlockHidden, async () => {
	// nextTick needed because screenWidth in parent is not set yet
	// thus making the isMobile value incorrect on which the logic isBlockHidden depends
		await nextTick();

		if (!isBlockHidden.value && !isInitialDataFetched.value) {
			getInitialData();
		}
	}, {
		immediate: true,
	});

	onMounted(() => {
		// Always fetch data on mount if not already fetched, regardless of isBlockHidden
		if (!isInitialDataFetched.value) {
			getInitialData();
		}
	});

	return {
		handleBrowserNavigationCategoryChange,
		handleCategoryClick,
		handleSortChange,
		handlePageChange,
		isEcommerceLoading,
		handleWindowPopState,
		pageCount,
		isProductListShown,
		totalProductCount,
		currentPage,
		legacyProductPages,
		currentCategoryId,
		sorting,
		variantsQuantity,
		isInitialDataFetched,
		isCategoriesLoaded,
		isProductReviewEnabled,
		canAddToCart,
		categories,
		products,
	};
};
